import { P2GAlert, P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useCallback, useEffect, useState } from 'react';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  IntegrationType,
  StrVal
} from '../../../lib/api/account-linking/types';
import { P2GProgress } from '@parcel2go/core-ui';
import { SumNumber } from '../lib/helpers/Array';
import { ValidationErrorMessages } from '../lib/helpers/ValidateIntegration';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import Verify from './Verify';
import ScreenSteps from './ScreenSteps';

interface props {
  integrationSlug: string;
  integrationType: IntegrationType;
  onComplete: (accountId: string, followUpAction?: ActionType) => void;
  action: ActionType;
  accountId: string;
  onError?: () => void;
  wrapper: (
    content: React.ReactNode,
    title?: string,
    subtitle?: string,
    sectionHeading?: string,
    icon?: React.ReactNode
  ) => React.ReactNode;
}

export default function ActionInstructionsScreens({
  integrationSlug,
  onError,
  action,
  integrationType,
  onComplete,
  accountId,
  wrapper
}: props) {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const { getActionInstructions, postActionValues } = useAccountLinkingApi();
  const [instructions, setInstructions] = useState<GetInstructionsApiResponseModel>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [canGetActions, setCanGetActions] = useState<boolean>(false);
  const [hasInputs, setHasInputs] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const getActions = useCallback(
    async (integrationType: IntegrationType, integrationSlug: string, action: ActionType, accountId: string) => {
      setLoading(true);
      const r = await getActionInstructions(integrationType, integrationSlug, action, accountId);
      if (!r.success && onError) {
        onError();
      }
      setCanGetActions(r.success);
      setInstructions(r.result);
      setHasInputs(SumNumber(r.result.steps.map((x) => x.inputs?.length || 0)) > 0);
      setLoading(false);
    },
    [getActionInstructions, onError]
  );

  useEffect(() => {
    if (!action) {
      setInstructions(null);
      setLoading(false);
      return;
    }
    getActions(integrationType, integrationSlug, action, accountId);
  }, [integrationType, integrationSlug, action, accountId, getActions]);

  const onSubmit = (values: StrVal) => {
    if (instructions?.readonly === true) {
      return;
    }

    setSaving(true);
    setErrorMessages([]);
    postActionValues(integrationType, integrationSlug, action, values, accountId).then((r) => {
      if (r.success) {
        if (r.result.redirectUrl) {
          document.location.href = r.result.redirectUrl;
        } else {
          let followUpAction: ActionType = r.result.accountStatus === 'ConfigurationRequired' ? 'Configure' : null;
          if (integrationType === 'KnowYourCustomer' && r.result.accountStatus === 'Pending') {
            followUpAction = 'Verify';
          }

          onComplete(r.result.accountId, followUpAction);
        }
      } else {
        setErrorMessages(ValidationErrorMessages(r.validationErrors, r.error?.code));
        setSaving(false);
      }
    });
  };

  const onAttempt = useCallback(async () => {
    const r = await postActionValues(integrationType, integrationSlug, action, {}, accountId);
    if (r.success && r.result.accountStatus === 'Active') {
      onComplete(r.result.accountId);
    } else {
      const instructionsResult = await getActionInstructions(integrationType, integrationSlug, action, accountId);
      if (instructionsResult.success) {
        setInstructions(instructionsResult.result);
      }
    }
  }, [postActionValues, integrationType, integrationSlug, action, accountId, onComplete, getActionInstructions]);

  if (!action) {
    return null;
  }

  if (loading) {
    return wrapper(
      <P2GBox textAlign="center" p={4}>
        <P2GProgress />
      </P2GBox>,
      'Loading'
    );
  }

  if (!canGetActions) {
    return wrapper(
      <P2GBox>
        <P2GText>{t('ACTION_UNAVAILABLE', { defaultValue: 'This action is unavailable.' })} </P2GText>
      </P2GBox>,
      'Action Unavailable'
    );
  }

  if (action === 'Verify') {
    return wrapper(
      <Verify
        onAttempt={onAttempt}
        integrationSlug={integrationSlug}
        action={action}
        pleaseNoteText={instructions?.pleaseNoteText}
      />
    );
  }

  return (
    <ScreenSteps
      hasInputs={hasInputs}
      integrationType={integrationType}
      integrationSlug={integrationSlug}
      action={action}
      steps={instructions.steps}
      terms={instructions.termsAndConditions}
      showInstructions={false}
      saving={saving}
      readonly={instructions.readonly}
      onSubmit={onSubmit}
      wrapper={(content, title, subtitle, sectionHeading, icon) =>
        wrapper(
          <>
            {content}
            {errorMessages.length > 0 && (
              <P2GBox mt={2}>
                <P2GAlert color="error">
                  {errorMessages.map((e) => (
                    <P2GText component="p" key={e}>
                      {e}
                    </P2GText>
                  ))}
                </P2GAlert>
              </P2GBox>
            )}
          </>,
          title,
          subtitle,
          sectionHeading,
          icon
        )
      }
      submissionButtonLabel={
        action === 'Configure'
          ? t('SAVE_AND_CONTINUE', { defaultValue: 'Save and Continue' })
          : action === 'Delete'
            ? t('CONFIRM_ACCOUNT_DELETE', { defaultValue: 'Confirm' })
            : null
      }
    />
  );
}
